/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import * as shortId from 'shortid'
import React, { useMemo, useCallback, useState } from 'react'
import {
  Button,
  Card,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  ResizeSensor,
  TextArea,
  Tooltip
} from '@blueprintjs/core'
import { defer, debounce } from 'lodash'
import { undoManager } from '../../../helper/functions'
import { getInitialRecordProperty } from '../../../logic/SM2+'
import { updateFragment } from '../../../backend/uploadType2/updateFragment'
import { ImageClass } from '../../addon/addons/parts/forEachInsertForm/imageHelp'
import { globalOnDemandVars } from '../../../helper/context'
import { CommonRender } from '../../addon/addons/parts/forEachInsertForm/common'
import { OverlayProps } from '../../../helper/configs'
import { useTranslation } from 'react-i18next'

export class Fragment {
  constructor ({ id, uploadType2Id, kind, textAnswer, learningRecord, timeStamp, cropConfig }) {
      this.id = id || shortId.generate()
    this.uploadType2Id = uploadType2Id
    this.kind = kind || 'media-answer'
    this.textAnswer = textAnswer || ''
    this.learningRecord = learningRecord || getInitialRecordProperty()
    this.timeStamp = timeStamp || Date.now()
    this.cropConfig = cropConfig
  }

  update (update) {
    if (update.kind) {
      this.kind = update.kind
    }
    if (update.textAnswer) {
      this.textAnswer = update.textAnswer || ''
    }
    if (update.learningRecord) {
      this.learningRecord = update.learningRecord
    }
    if (!globalOnDemandVars.isPixelMode()) {
      updateFragment(this.uploadType2Id, this.id, update)
    }
  }
}

export function FragmentElement ({ type, fragment, index, updateFragment, deleteFragment, showChange }) {
  const { t } = useTranslation()

  const [sort, setSort] = useState(fragment.kind)
  const [textAnswer, setTextAnswer] = useState(fragment.textAnswer)

  const originalContent = useMemo(() => (globalOnDemandVars.uploadType2s()[fragment.uploadType2Id].content), [fragment.uploadType2Id])
  const cropConfig = useMemo(() => (fragment.cropConfig), [fragment.cropConfig])
  const [contentSize, setContentSize] = useState({
    width: 0,
    height: 0
  })
  const croppedContent = useMemo(() => {
    let result
    if (typeof originalContent === 'object' && originalContent.isImage) {
      result = <CommonRender key={'croppedContent'} type={type} contentData={new ImageClass(originalContent.name, originalContent.publicId, originalContent.width, originalContent.height, cropConfig)} />
    } else {
      result = <div key={'croppedContent'} css={css`width: ${contentSize.width * cropConfig.width}px; height: ${contentSize.height * cropConfig.height}px; overflow: hidden; position: relative; display: inline-block;`}>
        <div css={css`position: absolute; top: ${-1 * (contentSize.height * cropConfig.y)}px; left: ${-1 * (contentSize.width * cropConfig.x)}px; display: inline-block;`}>
          <ResizeSensor onResize={(entries) => {
            const { width, height } = entries[0].contentRect
            setContentSize({
              width, height
            })
          }}><CommonRender type={type} contentData={originalContent} /></ResizeSensor>
        </div>
      </div>
    }
    return (result)
  }, [contentSize.height, contentSize.width, cropConfig, originalContent, type])

  const onGlobalChange = useCallback(
    debounce((nextValue) => {
      const prevValue = fragment.textAnswer
      updateFragment(fragment.id, {
        textAnswer: nextValue
      })
      // -----------------------------------------------------------------------------------------------------------
      undoManager.add({
        redo: () => {
          setTextAnswer(nextValue)
          updateFragment(fragment.id, {
            textAnswer: nextValue
          })
        },
        undo: () => {
          setTextAnswer(prevValue)
          updateFragment(fragment.id, {
            textAnswer: prevValue
          })
        }
      })
    }, 1000, { leading: true, trailing: true }
    ),
    []
  )

  const onChange1 = useCallback(
    (e) => {
      const nextValue = e.target.value
      setTextAnswer(nextValue)
      onGlobalChange(nextValue)
    },
    [onGlobalChange]
  )

  const onRadioChange = useCallback(
    (e) => {
      const nextValue = e.currentTarget.value
      const prevValue = fragment.kind
      setSort(nextValue)
      updateFragment(fragment.id, {
        kind: nextValue
      })
      // -----------------------------------------------------------------------------------------------------------
      undoManager.add({
        redo: () => {
          setSort(nextValue)
          updateFragment(fragment.id, {
            kind: nextValue
          })
        },
        undo: () => {
          setSort(prevValue)
          updateFragment(fragment.id, {
            kind: prevValue
          })
        }
      })
    }
    ,
    [fragment.id, fragment.kind, updateFragment]
  )

  return (
    <Card css={css`padding: 0; position: relative; background-color: var(--baseColor);`} elevation={2}>
      <div css={css`display: flex; justify-content: space-between;`}>
        <Tooltip {...OverlayProps} position={'top'} content={<div>
          {t('\'Media Answer\' is to cover part of the original media and guess what the part was.')}
          <br />
          {t('\'Text Answer\' is to look at a part of the original media and guess what the text you entered was.')}
        </div>}>

          <RadioGroup inline css={css`padding-top: var(--s-2); padding-left: var(--s-3);`} onChange={onRadioChange} selectedValue={sort}>
            <Radio label={t('Media Answer')} value={'media-answer'} />
            <Radio label={t('Text Answer')} value={'text-answer'} />
          </RadioGroup>
        </Tooltip>
        <Button icon={'cross'} onClick={() => {
          deleteFragment(fragment.id)
          defer(() => { showChange() })
        }} minimal />

      </div>

      <div css={css` position: relative;
// width: ${originalContent.height ? (originalContent.width * cropConfig.width) + 'px' : contentSize.width * cropConfig.width + 'px'}; min-width: 100%;
// height: ${originalContent.height ? (originalContent.height * cropConfig.height) + 'px' : contentSize.height * cropConfig.height + 'px'}; max-height: 200px;
max-width: 500px; max-height: 300px;
overflow: auto;
}
`}>
        {sort === 'media-answer' ? null : (
          <TextArea autoFocus key={'textarea'} value={textAnswer} onChange={onChange1} css={css`resize: none; z-index: 1; background-color: rgba(0, 0, 0, 0.6); color: var(--baseColor); caret-color: var(--baseColor); position: absolute;
width: 100% !important;
height: 100% !important;
`} />
        )}
        <div css={css`margin: auto; width: fit-content; padding: var(--s-2);`}>{croppedContent}</div>
      </div>
    </Card>
  )
}
